import { CONFIG_KEYS, type PublicUserConfig, type PublicUserConfigValue } from './types'

/**
 * Structural slice of `PublicUser` the config utilities depend on. Apps
 * with their own local user shape can pass it directly as long as it
 * exposes a `config` map.
 */
export interface UserWithConfig {
  config: PublicUserConfig
}

/**
 * Read a config value off `user.config` with optional typed fallback.
 * Returns the stored value if present, else `fallback`, else undefined.
 * Multipass parses values per their registered ConfigValueType, so the
 * scalar comes through already typed as string|boolean.
 */
export function getConfigValue<T extends PublicUserConfigValue>(
  user: UserWithConfig | null | undefined,
  key: string,
  fallback?: T,
): T | undefined {
  const raw = user?.config?.[key]
  if (raw === undefined) {
    return fallback
  }
  return raw as T
}

export type ColorModePreference = 'light' | 'dark' | 'system'

const COLOR_MODE_VALUES: ReadonlySet<ColorModePreference> = new Set(['light', 'dark', 'system'])

/**
 * Write-side guard, mirroring `getColorMode`'s read-side validation.
 * Multipass rejects an out-of-range value with a 400 (`color_mode` is a
 * registered ENUM key), so callers use this to fail locally instead of
 * round-tripping a value that can never be stored.
 */
export function isColorModePreference(value: unknown): value is ColorModePreference {
  return typeof value === 'string' && (COLOR_MODE_VALUES as Set<string>).has(value)
}

/**
 * Typed reader for the user's preferred color mode. Returns undefined
 * when the user has no preference set or the stored value is unknown,
 * letting callers fall back to their own default (typically `'system'`).
 */
export function getColorMode(user: UserWithConfig | null | undefined): ColorModePreference | undefined {
  const raw = getConfigValue<string>(user, CONFIG_KEYS.COLOR_MODE)
  return isColorModePreference(raw) ? raw : undefined
}
